import React, { useEffect, useState } from 'react';
import moment from 'moment-timezone';
import MomentUtils from '@date-io/moment';
import {
    MuiPickersUtilsProvider,
    KeyboardDatePicker,
} from '@material-ui/pickers';

import ActionButton from '../../Common/ActionButton';
import PanelBlock from '../../Common/PanelBlock';

import InputCurrencyField from '../../Fields/InputCurrencyField';
import Field from '../../Fields/Field';

import {
    queryReverseCredit,
    queryGetCreditBalances,
} from '../../../Queries';
import { getApplicationDateConfig, dateIsUnavailable } from '../../../Utils/applicationDate';
import { creditRefundAmountValidator } from '../../../Validators/Credit';

import featureFlags from '../../../featureFlags';
import './CreditRefundPanel.scss';

const CreditRefundPanel = ({
    credit,
    creditApplicationDate,
    onExit,
    facilityId,
    fieldConfiguration: { facilityTimezone, now },
}) => {
    const creditId = credit.fields.id.value;
    const isApplyCash = credit.fields.application_type.value === 'APPLY_CASH';
    const isDNAC = credit.fields.application_type.value === 'DO_NOT_APPLY_CASH';

    const defaults = {
        appDate: isApplyCash ? moment(now).tz(facilityTimezone) : null,
        taxes: isDNAC ? null : 0,
    };

    const [amount, setAmount] = useState(0);
    const [tax1, setTax1] = useState(defaults.taxes);
    const [tax2, setTax2] = useState(defaults.taxes);
    const [tax3, setTax3] = useState(defaults.taxes);
    const [tax4, setTax4] = useState(defaults.taxes);
    const [creditBalances, setCreditBalances] = useState({});

    const [applicationDate, setApplicationDate] = useState(defaults.appDate);
    const [applicationDateConfig, setApplicationDateConfig] = useState(null);
    const [noAppDatesAvailable, setNoAppDatesAvailable] = useState(false);

    const [amountIsValid, setAmountIsValid] = useState(true);
    const [tax1IsValid, setTax1IsValid] = useState(true);
    const [tax2IsValid, setTax2IsValid] = useState(true);
    const [tax3IsValid, setTax3IsValid] = useState(true);
    const [tax4IsValid, setTax4IsValid] = useState(true);

    const [initialized, setInitialized] = useState(false);
    const [saveInProgress, setSaveInProgress] = useState(false);

    useEffect(() => {
        const getCreditBalances = async () => {
            try {
                const response = await queryGetCreditBalances({ creditId });
                if (response.errors) {
                    console.log(response.errors);
                    alert('Error loading credit refund panel');
                    onExit();
                }

                const balances = response.data.getCreditBalances;
                setCreditBalances(balances);
                setAmount(balances.amount);

                if (!isDNAC) {
                    setTax1(balances.amount_tax_1);
                    setTax2(balances.amount_tax_2);
                    setTax3(balances.amount_tax_3);
                    setTax4(balances.amount_tax_4);
                }
            } catch (error) {
                console.log(error);
                alert('Error loading credit refund panel');
                onExit();
            }
        };

        const fetchApplicationDateConfig = async () => {
            if (!isApplyCash) return;

            try {
                const appDateConfig = await getApplicationDateConfig(facilityId);
                setApplicationDateConfig(appDateConfig);
                checkAppDateAvailability(appDateConfig);
                if (defaults.appDate.isBefore(creditApplicationDate)) {
                    setApplicationDate(creditApplicationDate);
                }
            } catch (error) {
                console.log(error);
                alert('Error loading credit refund panel');
                onExit();
            }
        }

        const initialize = async () => {
            await Promise.all([
                getCreditBalances(),
                fetchApplicationDateConfig(),
            ]);
            setInitialized(true);
        };

        initialize();
    }, []);

    const checkAppDateAvailability = (appDateConfig) => {
        const { maxApplicationDate } = appDateConfig;
        if (!maxApplicationDate) return;
        if (maxApplicationDate.isBefore(creditApplicationDate, 'day')) setNoAppDatesAvailable(true);
    };

    const shouldDisableDate = (date) => {
        const isUnavailable = dateIsUnavailable(date, applicationDateConfig, facilityTimezone);
        const isBeforeCreditAppDate = date.isBefore(creditApplicationDate, 'day');

        return isUnavailable || isBeforeCreditAppDate;
    };

    const getGrossAmount = () => (
        amount +
        (tax1 ?? 0) +
        (tax2 ?? 0) +
        (tax3 ?? 0) +
        (tax4 ?? 0)
    );

    const handleSave = async () => {
        setSaveInProgress(true);

        try {
            const response = await queryReverseCredit({
                creditId,
                applicationDate: applicationDate?.format('YYYY-MM-DD') ?? null,
                amount,
                amountTax1: tax1 ?? defaults.taxes,
                amountTax2: tax2 ?? defaults.taxes,
                amountTax3: tax3 ?? defaults.taxes,
                amountTax4: tax4 ?? defaults.taxes,
            });

            if (response.errors) {
                console.log(response.errors);
                setSaveInProgress(false);
                alert('Error saving credit refund/reversal');
                return;
            }

            onExit();
        } catch (error) {
            console.log(error);
            setSaveInProgress(false);
            alert('Error saving credit refund/reversal');
        }
    }

    const amountValidatorText = (
        <div className='parkchirp--credit-refund-panel--error-amountValidatorText'>
            Invalid Net Amount.
        </div>
    );

    const taxValidatorText = (
        <div className='parkchirp--credit-refund-panel--error-amountValidatorText'>
            Invalid Tax Amount.
        </div>
    );

    const grossAmount = getGrossAmount();

    const disableSave = !(
        amountIsValid &&
        tax1IsValid &&
        tax2IsValid &&
        tax3IsValid &&
        tax4IsValid &&
        (grossAmount > 0)
    );

    if (!initialized) return <></>;
    return (
        <div className='parkchirp--credit-refund-panel'>
            <PanelBlock
                heading={'Refund/Reverse Credit'}
                actions={[
                    <ActionButton
                        id='parkchirp--credit-refund-action-button'
                        label='Save'
                        onClick={handleSave}
                        inProgressState={saveInProgress}
                        disabled={disableSave || (isApplyCash && !applicationDate)}
                    />,
                    <button
                        type='button'
                        className='parkchirp--button-as-link'
                        style={{ paddingLeft: '10px', paddingRight: '10px' }}
                        onClick={onExit}
                    >
                        Cancel
                    </button>,
                ]}
            >
                <div className='parkchirp--credit-refund-panel--main-inputs-container'>
                    <Field
                        label='Net Amount'
                        id='parkchirp--refund--payment-id'
                        containerClasses={['parkchirp--refund-input-block']}
                        layout='stacked'
                    >
                        <InputCurrencyField
                            inputId={`net--amount`}
                            value={amount}
                            editMode={true}
                            onChange={setAmount}
                            negativeMode={false}
                            dashForNull={true}
                            validator={creditRefundAmountValidator(creditBalances.amount, setAmountIsValid)}
                            validatorText={amountValidatorText}
                        />
                    </Field>
                    <Field
                        label='Tax 1'
                        id='parkchirp--refund--payment-id'
                        containerClasses={[
                            `parkchirp--refund-input-block ${
                                !isApplyCash ? 'apply-cash' : ''
                            }`,
                        ]}
                        layout='stacked'
                    >
                        <InputCurrencyField
                            inputId={`amount--tax-1`}
                            value={tax1}
                            editMode={!isDNAC}
                            onChange={setTax1}
                            negativeMode={false}
                            dashForNull={true}
                            validator={creditRefundAmountValidator(creditBalances.amount_tax_1, setTax1IsValid)}
                            validatorText={taxValidatorText}
                        />
                    </Field>
                    <Field
                        label='Tax 2'
                        id='parkchirp--refund--payment-id'
                        containerClasses={[
                            `parkchirp--refund-input-block ${
                                !isApplyCash ? 'apply-cash' : ''
                            }`,
                        ]}
                        layout='stacked'
                    >
                        <InputCurrencyField
                            inputId={`amount--tax-2`}
                            value={tax2}
                            editMode={!isDNAC}
                            onChange={setTax2}
                            negativeMode={false}
                            dashForNull={true}
                            validator={creditRefundAmountValidator(creditBalances.amount_tax_2, setTax2IsValid)}
                            validatorText={taxValidatorText}
                        />
                    </Field>
                    <Field
                        label='Tax 3'
                        id='parkchirp--refund--payment-id'
                        containerClasses={[
                            `parkchirp--refund-input-block ${
                                !isApplyCash ? 'apply-cash' : ''
                            }`,
                        ]}
                        layout='stacked'
                    >
                        <InputCurrencyField
                            inputId={`amount--tax-3`}
                            value={tax3}
                            editMode={!isDNAC}
                            onChange={setTax3}
                            negativeMode={false}
                            dashForNull={true}
                            validator={creditRefundAmountValidator(creditBalances.amount_tax_3, setTax3IsValid)}
                            validatorText={taxValidatorText}
                        />
                    </Field>
                    <Field
                        label='Tax 4'
                        id='parkchirp--refund--payment-id'
                        containerClasses={[
                            ` parkchirp--refund-input-block ${
                                !isApplyCash ? 'apply-cash' : ''
                            }`,
                        ]}
                        layout='stacked'
                    >
                        <InputCurrencyField
                            inputId={`amount--tax-4`}
                            value={tax4}
                            editMode={!isDNAC}
                            onChange={setTax4}
                            negativeMode={false}
                            dashForNull={true}
                            validator={creditRefundAmountValidator(creditBalances.amount_tax_4, setTax4IsValid)}
                            validatorText={taxValidatorText}
                        />
                    </Field>
                    <Field
                        label='Gross Amount'
                        id='parkchirp--refund--payment-id'
                        containerClasses={['parkchirp--refund-input-block']}
                        layout='stacked'
                    >
                        <InputCurrencyField
                            inputId={`gross-amount`}
                            value={grossAmount}
                            editMode={false}
                            negativeMode={false}
                            dashForNull={true}
                        />
                    </Field>
                    {featureFlags.REVERSE_APPLICATIONDATE && (
                        <Field
                            label='Application Date'
                            id='parkchirp--payment--application-date'
                            containerClasses={['parkchirp--payment-input-block']}
                            layout='stacked'
                        >
                            {isApplyCash ?
                                <MuiPickersUtilsProvider utils={MomentUtils}>
                                    <KeyboardDatePicker
                                        id='parkchirp--credit-increase--application-date'
                                        variant='outlined'
                                        value={applicationDate}
                                        onChange={setApplicationDate}
                                        format='M/D/YYYY'
                                        autoOk={true}
                                        shouldDisableDate={shouldDisableDate}
                                    />
                                </MuiPickersUtilsProvider>
                                :
                                <span>-</span>
                            }
                            {!applicationDate && isApplyCash && (
                                <span className='error'>Provide a valid date</span>
                            )}
                            {noAppDatesAvailable && (
                                <span className='error'>
                                    There are no application dates available. Please reopen an
                                    accounting period to make this reversal.
                                </span>
                            )}
                        </Field>
                    )}
                </div>
            </PanelBlock>
        </div>
    );
};

export default CreditRefundPanel;
